import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Spacer,
} from "../../../../components";
import { FC } from "react";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { ProjectProductMeasurement, SelectMaterialInput, PreferredMaterialQualityInput } from "../../../../redux/types/Material";
import { ArrowBack } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { SELECT_MATERIAL, MATERIAL_QUALITY } from "../../../../apolloClient/operation/mutations/mutation";
import { useParams } from "react-router-dom";

interface MaterialShopProps {
  handleRefetchProjectDetails: Function;
  projectData?: CustomerProjectDetails;
  selectedProduct: ProjectProductMeasurement;
  onBack: () => void;
}

enum Quality {
  Low = "LOW",
  Medium = "MEDIUM",
  High = "HIGH",
}

interface PriceRangeOption {
  value: string;
  label: string;
  min: number;
  max: number;
}

const DEFAULT_PRICE_RANGE: PriceRangeOption = {
  value: "all",
  label: "All price ranges",
  min: 0,
  max: Number.POSITIVE_INFINITY,
};

interface FiltersState {
  priceRange: PriceRangeOption;
  selectedBrands: string[];
  selectedColors: string[];
  selectedTags: string[];
}

const toNumericPrice = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getMaterialUnitPrice = (material: any): number | null => {
  if (!material) return null;
  return (
    toNumericPrice(material?.UnitPrice) ??
    toNumericPrice(material?.unitPrice) ??
    toNumericPrice(material?.price)
  );
};

const getMaterialTotalPrice = (material: any): number => {
  return toNumericPrice(material?.price) ?? 0;
};

export const MaterialShop: FC<MaterialShopProps> = ({
  handleRefetchProjectDetails,
  projectData,
  selectedProduct,
  onBack,
}) => {
  const { id: projectId } = useParams<{ id: string }>();
  const [quality, setQuality] = useState<Quality>((projectData?.preferredQuality as Quality) ?? Quality.Medium);
  const [selectedMeasurement, setSelectedMeasurement] = useState<any>(null);
  const [measurementId, setMeasurementId] = useState<number>(0);
  const [selectingMaterialId, setSelectingMaterialId] = useState<number | null>(null);
  const autoSelectionInProgress = useRef<boolean>(false);
  const topRef = useRef<HTMLDivElement>(null);
  
  // State to track selected variants for each material
  const [selectedVariants, setSelectedVariants] = useState<{[materialId: number]: number}>({});
  
  // State for scroll positions of each category
  const [scrollPositions, setScrollPositions] = useState<{[categoryKey: string]: number}>({});
  
  // State to track which categories have overflow
  const [hasOverflow, setHasOverflow] = useState<{[categoryKey: string]: boolean}>({});
  
  // State for material detail popup
  const [selectedMaterialForPopup, setSelectedMaterialForPopup] = useState<any>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState<FiltersState>({
    priceRange: DEFAULT_PRICE_RANGE,
    selectedBrands: [] as string[],
    selectedColors: [] as string[],
    selectedTags: [] as string[],
  });
  const [showFilters, setShowFilters] = useState(false);

  // Apollo mutations
  const [selectMaterial] = useMutation<
    { selectMaterial: boolean },
    { input: SelectMaterialInput }
  >(SELECT_MATERIAL);

  const [preferredMaterialQuality] = useMutation<
    { preferredMaterialQuality: boolean },
    { input: PreferredMaterialQualityInput }
  >(MATERIAL_QUALITY);

  const qualityLevels = [Quality.Low, Quality.Medium, Quality.High];
  
  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };
  
  // Helper function to get quality display label
  const getQualityLabel = (quality: Quality): string => {
    switch (quality) {
      case Quality.Low:
        return "Standard";
      case Quality.Medium:
        return "Mid-tier";
      case Quality.High:
        return "Best";
      default:
        return quality;
    }
  };
  
  // Function to check if container has overflow
  const checkOverflow = (categoryKey: string) => {
    const container = document.getElementById(`scroll-container-${categoryKey}`);
    if (container) {
      const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
      setHasOverflow(prev => {
        if (prev[categoryKey] === hasHorizontalOverflow) return prev;
        return { ...prev, [categoryKey]: hasHorizontalOverflow };
      });
    }
  };
  
  // Get current materials for the selected measurement
  const currentMaterials = useMemo(() => {
    if (!selectedMeasurement?.associatedMaterials) return [];
    return selectedMeasurement.associatedMaterials;
  }, [selectedMeasurement]);
  
  // Calculate dynamic price range options
  const priceRangeOptions = useMemo<PriceRangeOption[]>(() => {
    if (!currentMaterials.length) {
      return [DEFAULT_PRICE_RANGE];
    }

    const unitPrices = currentMaterials
      .map((material: any) => getMaterialUnitPrice(material))
      .filter((price): price is number => price !== null);

    if (unitPrices.length === 0) {
      return [DEFAULT_PRICE_RANGE];
    }

    const uniquePrices = (Array.from(new Set(unitPrices)) as number[]).sort((a, b) => a - b);
    const bucketCount = Math.min(4, uniquePrices.length);
    const step = Math.ceil(uniquePrices.length / bucketCount);

    const dynamicOptions: PriceRangeOption[] = [DEFAULT_PRICE_RANGE];

    for (let i = 0; i < bucketCount; i++) {
      const startIdx = i * step;
      if (startIdx >= uniquePrices.length) break;

      const endIdx = Math.min(startIdx + step - 1, uniquePrices.length - 1);
      const min = uniquePrices[startIdx];
      const max = uniquePrices[endIdx];

      const label = min === max
        ? `$${formatCurrency(min)}`
        : `$${formatCurrency(min)} - $${formatCurrency(max)}`;

      const value = min === max ? `${min}` : `${min}-${max}`;

      if (!dynamicOptions.some((option) => option.value === value)) {
        dynamicOptions.push({ value, label, min, max });
      }
    }

    return dynamicOptions;
  }, [currentMaterials]);
  
  // Filter materials function
  const filterMaterials = useCallback((materials: any[]) => {
    if (!materials || materials.length === 0) return [];

    const filtered = materials.filter(material => {
      // Brand filter
      if (filters.selectedBrands.length > 0) {
        if (!material.brand || !filters.selectedBrands.includes(material.brand)) {
          return false;
        }
      }

      // Price filter
      const hasPriceFilter = filters.priceRange.value !== DEFAULT_PRICE_RANGE.value;
      if (hasPriceFilter) {
        const materialPrice = getMaterialUnitPrice(material);
        if (
          materialPrice === null ||
          materialPrice < filters.priceRange.min ||
          materialPrice > filters.priceRange.max
        ) {
          return false;
        }
      }

      // Color filter
      if (filters.selectedColors.length > 0) {
        const materialColors = material.variants?.map((v: any) => v.color) || [];
        const hasMatchingColor = filters.selectedColors.some(selectedColor =>
          materialColors.includes(selectedColor)
        );
        if (!hasMatchingColor) {
          return false;
        }
      }

      // Tags filter
      if (filters.selectedTags.length > 0) {
        const materialTags = material.tags?.map((tag: any) => tag.tagName) || [];
        const hasMatchingTag = filters.selectedTags.some(selectedTag =>
          materialTags.includes(selectedTag)
        );
        if (!hasMatchingTag) {
          return false;
        }
      }

      return true;
    });

    // Sort by price (lowest to highest)
    const sorted = filtered.sort((a, b) => {
      const priceA = getMaterialUnitPrice(a) ?? Number.POSITIVE_INFINITY;
      const priceB = getMaterialUnitPrice(b) ?? Number.POSITIVE_INFINITY;
      return priceA - priceB;
    });

    return sorted;
  }, [filters]);
  
  // Functions for popup modal
  const openMaterialPopup = useCallback((material: any) => {
    setSelectedMaterialForPopup(material);
    setIsPopupOpen(true);
  }, []);
  
  const closePopup = useCallback(() => {
    setIsPopupOpen(false);
    setSelectedMaterialForPopup(null);
  }, []);

  // Sync quality state with backend preferred quality
  useEffect(() => {
    if (projectData?.preferredQuality) {
      setQuality(projectData.preferredQuality as Quality);
    }
  }, [projectData?.preferredQuality]);

  // Scroll to top when selectedMeasurement changes
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Fallback to window scroll if ref is not available
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [selectedMeasurement]);

  // Get measurements that require customer selection
  const requiredSelectionMeasurements = selectedProduct.measurements?.filter(
    (measure: any) => measure.forceCustomerSelection === true
  ) || [];

  // Get measurements that are optional
  const optionalSelectionMeasurements = selectedProduct.measurements?.filter(
    (measure: any) => measure.forceCustomerSelection === false
  ) || [];

  // Fallback: if no measurements have forceCustomerSelection property, show all as optional
  const allMeasurements = selectedProduct.measurements || [];
  const showAllAsOptional = allMeasurements.length > 0 && 
    requiredSelectionMeasurements.length === 0 && 
    optionalSelectionMeasurements.length === 0;

  // Auto-select materials based on quality level for optional selections
  useEffect(() => {
    if (selectedProduct?.measurements && projectId && !autoSelectionInProgress.current) {
      autoSelectionInProgress.current = true;
      
      // Auto-select measurements that have only one associated material (regardless of forceCustomerSelection)
      selectedProduct.measurements.forEach((measure: any) => {
        const associatedMaterials = measure?.associatedMaterials || [];
        
        // If there's exactly one material and it's not already selected, auto-select it
        if (
          Array.isArray(associatedMaterials) &&
          associatedMaterials.length === 1 &&
          !associatedMaterials.some((mat: any) => mat.isSelected)
        ) {
          const material = associatedMaterials[0];
          if (material.id > 0 && measure.id > 0) {
            selectMaterial({
              variables: {
                input: {
                  projectId: parseInt(projectId, 10),
                  productId: selectedProduct.productId,
                  materialId: material.id,
                  measurementId: measure.id,
                },
              },
            })
              .then(() => {
                handleRefetchProjectDetails();
              })
              .catch((error) => {
                console.error("Error auto-selecting single material:", error);
              });
          }
        }
      });

      // Get all measurements that don't require customer selection (auto-selectable)
      const autoSelectableMeasurements = selectedProduct.measurements.filter(
        (measure: any) => measure?.forceCustomerSelection === false
      );

      // Iterate through auto-selectable measurements and handle selection based on quality hierarchy
      autoSelectableMeasurements.forEach((measure: any) => {
        const associatedMaterials = measure?.associatedMaterials || [];
        
        // Skip if no materials or if already has a selection or if only one material (handled above)
        if (associatedMaterials.length === 0 || 
            associatedMaterials.length === 1 ||
            associatedMaterials.some((mat: any) => mat.isSelected)) {
          return;
        }

        let filteredMaterials = associatedMaterials.filter(
          (material: any) =>
            material?.quality?.toUpperCase() === quality.toUpperCase()
        );

        // If no materials match the selected quality, fallback to lower quality, then higher
        if (filteredMaterials.length === 0) {
          const qualityIndex = qualityLevels.indexOf(quality);

          // Fallback to lower quality first (if available)
          if (qualityIndex > 0) {
            filteredMaterials = associatedMaterials.filter(
              (material: any) =>
                material?.quality?.toUpperCase() ===
                qualityLevels[qualityIndex - 1].toUpperCase()
            );
          }

          // If lower quality is not available, fallback to the next higher quality
          if (
            filteredMaterials.length === 0 &&
            qualityIndex < qualityLevels.length - 1
          ) {
            filteredMaterials = associatedMaterials.filter(
              (material: any) =>
                material?.quality?.toUpperCase() ===
                qualityLevels[qualityIndex + 1].toUpperCase()
            );
          }
        }

        // Select the first material that matches the quality criteria
        if (filteredMaterials.length > 0) {
          const materialToSelect = filteredMaterials[0];
          
          selectMaterial({
            variables: {
              input: {
                projectId: parseInt(projectId, 10),
                productId: selectedProduct.productId,
                materialId: materialToSelect.id,
                measurementId: measure.id,
              },
            },
          })
            .then(() => {
              // Refetch project details to update the UI
              handleRefetchProjectDetails();
            })
            .catch((error) => {
              console.error("Error auto-selecting material:", error);
              // Don't show toast for auto-selection errors to avoid spam
            });
        }
      });
      
      // Reset the flag after a short delay to allow for future auto-selections if needed
      setTimeout(() => {
        autoSelectionInProgress.current = false;
      }, 1000);
    }
  }, [quality, selectedProduct?.measurements, projectId, selectMaterial, handleRefetchProjectDetails]);

  const handleQualityChange = useCallback(async (newQuality: Quality) => {
    setQuality(newQuality);
    
    if (!projectId) {
      toast("Project ID not found", {
        type: "error",
        position: "top-right",
      });
      return;
    }
    
    try {
      await preferredMaterialQuality({
        variables: {
          input: {
            projectId: parseInt(projectId, 10),
            quality: newQuality,
          },
        },
      });
      
      toast("Quality preference updated successfully!", {
        type: "success",
        position: "top-right",
      });
      
      // Reset auto-selection flag to allow re-running auto-selection with new quality
      autoSelectionInProgress.current = false;
      
      // Refetch project details to get updated data and trigger auto-selection with new quality
      await handleRefetchProjectDetails();
    } catch (error) {
      console.error("Error updating quality preference:", error);
      toast("Failed to update quality preference", {
        type: "error",
        position: "top-right",
      });
    }
  }, [projectId, preferredMaterialQuality, handleRefetchProjectDetails]);

  const handleMaterialSelection = useCallback((measurementData: any) => {
    if (!measurementData.associatedMaterials || measurementData.associatedMaterials.length === 0) {
      toast("No associated materials found for this measurement.", {
        type: "error",
        position: "top-right",
      });
      return;
    }
    setSelectedMeasurement(measurementData);
    setMeasurementId(measurementData.id);
  }, []);

  const chooseMaterial = useCallback(async (materialId: number, variantId?: number | null) => {
    if (materialId <= 0 || measurementId <= 0) {
      return;
    }
    
    if (!projectId) {
      toast("Project ID not found", {
        type: "error",
        position: "top-right",
      });
      return;
    }
    
    setSelectingMaterialId(materialId);
    
    try {
      const input: any = {
        projectId: parseInt(projectId, 10),
        productId: selectedProduct.productId,
        materialId: materialId,
        measurementId: measurementId,
      };
      
      // Include variant ID if provided
      if (variantId) {
        input.materialVarientId = variantId;
      }
      
      await selectMaterial({
        variables: { input },
      });
      
      toast("Material selected successfully!", {
        type: "success",
        position: "top-right",
      });
      
      // Refetch project details to get updated data
      await handleRefetchProjectDetails();
      
      // Return to measurements list after successful selection
      setSelectedMeasurement(null);
    } catch (error) {
      console.error("Error selecting material:", error);
      toast("Failed to select material", {
        type: "error",
        position: "top-right",
      });
    } finally {
      setSelectingMaterialId(null);
    }
  }, [measurementId, handleRefetchProjectDetails, projectId, selectedProduct.productId, selectMaterial]);

  const renderMeasurementCard = (measure: any, isRequired: boolean) => {
    const hasSelection = measure.associatedMaterials?.some((material: any) => material.isSelected === true);
    const hasAssociatedMaterials = measure.associatedMaterials && measure.associatedMaterials.length > 0;
    const isMobile = window.innerWidth < 768;
    
    return (
      <div
        key={measure.id}
        style={{
          border: "2px solid #d5d5d5",
          width: "100%",
          marginBottom: "16px",
          minHeight: "100px",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            padding: isMobile ? "16px" : "16px 40px",
            gap: isMobile ? "12px" : "16px",
          }}
        >
          <div style={{ color: "#1f2937", flex: 1 }}>
            <div style={{ fontSize: isMobile ? "15px" : "16px", fontWeight: "500", marginBottom: "4px", wordBreak: "break-word" }}>
              {measure.customerDisplayName || measure.name}
            </div>
            <div style={{ fontSize: isMobile ? "13px" : "14px", color: "#6b7280" }}>
              Quantity: {measure.value?.quantity || 0} {measure.unit || "units"}
            </div>
            {!hasAssociatedMaterials && (
              <div style={{ fontSize: "12px", color: "#f59e0b", marginTop: "4px" }}>
                No materials configured
              </div>
            )}
          </div>
          <button
            onClick={() => handleMaterialSelection(measure)}
            disabled={!hasAssociatedMaterials}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              padding: isMobile ? "12px 20px" : "0 40px",
              height: isMobile ? "auto" : "40px",
              width: isMobile ? "100%" : "auto",
              cursor: hasAssociatedMaterials ? "pointer" : "not-allowed",
              border: "1px solid #1f2937",
              backgroundColor: hasSelection ? "#1f2937" : hasAssociatedMaterials ? "#fff" : "#f3f4f6",
              color: hasSelection ? "#fff" : hasAssociatedMaterials ? "#1f2937" : "#9ca3af",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
              opacity: hasAssociatedMaterials ? 1 : 0.6,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!hasSelection && hasAssociatedMaterials) {
                e.currentTarget.style.backgroundColor = "#1f2937";
                e.currentTarget.style.color = "#fff";
              }
            }}
            onMouseLeave={(e) => {
              if (!hasSelection && hasAssociatedMaterials) {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.color = "#1f2937";
              }
            }}
          >
            {!hasAssociatedMaterials 
              ? "No materials" 
              : hasSelection 
                ? "Change selection" 
                : "Select material"
            }
          </button>
        </div>
        
        <div style={{ borderTop: "1px solid #e1e1e1", width: "90%", margin: "0 5%" }} />
        
        <div style={{ padding: isMobile ? "16px" : "16px 40px" }}>
          {!hasSelection ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #c2d9ff",
                minWidth: isMobile ? "auto" : "150px",
                maxWidth: isMobile ? "100%" : "250px",
                width: isMobile ? "100%" : "auto",
                justifyContent: "center",
                borderRadius: "6px",
                padding: "8px 16px",
                gap: "8px",
                boxSizing: "border-box",
              }}
            >
              <div style={{ width: "15px", height: "15px", flexShrink: 0 }}>
                <img
                  src="https://imagedelivery.net/ljlAKBP_91m1yO7rrhjKgQ/b444b642-8093-4b48-e5e4-8304433f6e00/chatImage"
                  alt="No selection"
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
              <span style={{ fontSize: "14px", color: "#1f2937" }}>No selection</span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #c2d9ff",
                backgroundColor: "#c2d9ff",
                minWidth: isMobile ? "auto" : "150px",
                maxWidth: isMobile ? "100%" : "250px",
                width: isMobile ? "100%" : "auto",
                borderRadius: "6px",
                padding: "8px 16px",
                gap: "8px",
                boxSizing: "border-box",
              }}
            >
              <div style={{ width: "15px", height: "15px", flexShrink: 0 }}>
                <img
                  src="https://imagedelivery.net/ljlAKBP_91m1yO7rrhjKgQ/5f518614-6c2b-40c0-1e12-8e96fff80900/chatImage"
                  alt="Material selected"
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
              <span style={{ fontSize: "14px", color: "#1f2937" }}>Material selected</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (selectedMeasurement) {
    // Group materials by quality
    const materialsByQuality = (selectedMeasurement?.associatedMaterials || []).reduce((acc: any, material: any) => {
      const qualityKey = material.quality?.toUpperCase() || 'MEDIUM';
      if (!acc[qualityKey]) acc[qualityKey] = [];
      acc[qualityKey].push(material);
      return acc;
    }, {});

    // Quality configuration
    const qualityConfig = [
      { key: 'HIGH', label: 'Best Materials', description: 'Premium materials with best durability and finish' },
      { key: 'MEDIUM', label: 'Mid-tier Materials', description: 'Good balance of quality and value' },
      { key: 'LOW', label: 'Standard Materials', description: 'Budget-friendly options' }
    ];

    return (
      <div
        ref={topRef}
        style={{
          padding: window.innerWidth < 768 ? "16px" : "24px",
          backgroundColor: "#f8fafc",
          minHeight: "100%",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Back Button and Breadcrumb */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "24px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => {
              setSelectedMeasurement(null);
              setFilters({
                priceRange: DEFAULT_PRICE_RANGE,
                selectedBrands: [],
                selectedColors: [],
                selectedTags: [],
              });
              setShowFilters(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "6px",
              backgroundColor: "#3b82f6",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            <ArrowBack style={{ width: "20px", height: "20px" }} />
            Back
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span 
              style={{ fontSize: "14px", color: "#1f2937", cursor: "pointer" }}
              onClick={onBack}
            >
              Material selection /
            </span>
            <span 
              style={{ fontSize: "14px", color: "#1f2937", cursor: "pointer" }}
              onClick={() => setSelectedMeasurement(null)}
            >
              {selectedProduct.name} /
            </span>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              {selectedMeasurement.customerDisplayName || selectedMeasurement.name}
            </span>
          </div>
        </div>

        {/* Filter Bar */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <h2 style={{ fontSize: "18px", fontWeight: "500", color: "#1f2937", margin: 0 }}>
              Material Filters
            </h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "8px",
                border: showFilters ? "1px solid #3b82f6" : "1px solid #d1d5db",
                backgroundColor: showFilters ? "#3b82f6" : "#fff",
                color: showFilters ? "#fff" : "#3b82f6",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s ease",
              }}
            >
              <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
            </button>
          </div>

          {showFilters && (
            <div style={{ backgroundColor: "#f8f9fa", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px" }}>
              {/* Active Filters */}
              {(filters.selectedBrands.length > 0 || filters.selectedColors.length > 0 || filters.selectedTags.length > 0 || filters.priceRange.value !== DEFAULT_PRICE_RANGE.value) && (
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", paddingBottom: "12px", borderBottom: "1px solid #e5e7eb", marginBottom: "12px" }}>
                  <span style={{ fontSize: "14px", fontWeight: "500", color: "#6b7280" }}>Active filters:</span>

                  {filters.priceRange.value !== DEFAULT_PRICE_RANGE.value && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#3b82f6", color: "#fff", padding: "4px 8px", borderRadius: "16px", fontSize: "12px" }}>
                      <span>{filters.priceRange.label}</span>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, priceRange: DEFAULT_PRICE_RANGE }))}
                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "0 2px", fontSize: "14px" }}
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {filters.selectedBrands.map(brand => (
                    <div key={brand} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#3b82f6", color: "#fff", padding: "4px 8px", borderRadius: "16px", fontSize: "12px" }}>
                      <span>{brand}</span>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, selectedBrands: prev.selectedBrands.filter(b => b !== brand) }))}
                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "0 2px", fontSize: "14px" }}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {filters.selectedColors.map(color => (
                    <div key={color} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#3b82f6", color: "#fff", padding: "4px 8px", borderRadius: "16px", fontSize: "12px" }}>
                      <span>{color}</span>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, selectedColors: prev.selectedColors.filter(c => c !== color) }))}
                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "0 2px", fontSize: "14px" }}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {filters.selectedTags.map(tag => (
                    <div key={tag} style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: "#3b82f6", color: "#fff", padding: "4px 8px", borderRadius: "16px", fontSize: "12px" }}>
                      <span>{tag}</span>
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, selectedTags: prev.selectedTags.filter(t => t !== tag) }))}
                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "0 2px", fontSize: "14px" }}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={() => setFilters({ priceRange: DEFAULT_PRICE_RANGE, selectedBrands: [], selectedColors: [], selectedTags: [] })}
                    style={{ fontSize: "12px", color: "#6b7280", textDecoration: "underline", background: "none", border: "none", cursor: "pointer", marginLeft: "8px" }}
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Filter Dropdowns */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                {/* Price Range */}
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                    Price Range
                  </label>
                  <select
                    value={filters.priceRange.value}
                    onChange={(e) => {
                      const option = priceRangeOptions.find(opt => opt.value === e.target.value) || DEFAULT_PRICE_RANGE;
                      setFilters(prev => ({ ...prev, priceRange: option }));
                    }}
                    style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", backgroundColor: "#fff" }}
                  >
                    {priceRangeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Brand Filter */}
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                    Manufacturer
                  </label>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !filters.selectedBrands.includes(e.target.value)) {
                        setFilters(prev => ({ ...prev, selectedBrands: [...prev.selectedBrands, e.target.value] }));
                      }
                    }}
                    style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", backgroundColor: "#fff" }}
                  >
                    <option value="" disabled>Select Manufacturer...</option>
                    {(() => {
                      const uniqueBrands = Array.from(new Set(
                        currentMaterials.map((m: any) => m.brand).filter(Boolean)
                      ));
                      return uniqueBrands.map(brand => (
                        <option key={brand as string} value={brand as string}>{brand as string}</option>
                      ));
                    })()}
                  </select>
                </div>

                {/* Color Filter */}
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                    Color
                  </label>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !filters.selectedColors.includes(e.target.value)) {
                        setFilters(prev => ({ ...prev, selectedColors: [...prev.selectedColors, e.target.value] }));
                      }
                    }}
                    style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", backgroundColor: "#fff" }}
                  >
                    <option value="" disabled>Select color...</option>
                    {(() => {
                      const uniqueColors = Array.from(new Set(
                        currentMaterials.flatMap((m: any) => m.variants?.map((v: any) => v.color) || []).filter(Boolean)
                      ));
                      return uniqueColors.map(color => (
                        <option key={color as string} value={color as string}>{color as string}</option>
                      ));
                    })()}
                  </select>
                </div>

                {/* Tags Filter */}
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "4px" }}>
                    Tags
                  </label>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !filters.selectedTags.includes(e.target.value)) {
                        setFilters(prev => ({ ...prev, selectedTags: [...prev.selectedTags, e.target.value] }));
                      }
                    }}
                    style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", backgroundColor: "#fff" }}
                  >
                    <option value="" disabled>Select tag...</option>
                    {(() => {
                      const uniqueTags = Array.from(new Set(
                        currentMaterials.flatMap((m: any) => m.tags?.map((t: any) => t.tagName) || []).filter(Boolean)
                      ));
                      return uniqueTags.map(tag => (
                        <option key={tag as string} value={tag as string}>{tag as string}</option>
                      ));
                    })()}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Materials by Quality Category */}
        <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {qualityConfig.map(({ key, label, description }) => {
            const allMaterials = materialsByQuality[key] || [];
            const materials = filterMaterials(allMaterials);
            if (materials.length === 0) return null;

            return (
              <div key={key}>
                {/* Category Header */}
                <div style={{ marginBottom: "16px" }}>
                  <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#1f2937", margin: 0 }}>
                    {label}
                  </h3>
                  <p style={{ fontSize: "14px", color: "#6b7280", margin: "4px 0 0 0" }}>
                    {description}
                  </p>
                </div>

                {/* Horizontal Scrollable List */}
                <div style={{ position: "relative" }}>
                  {/* Scroll Arrows */}
                  {hasOverflow[key] && (
                    <>
                      <button
                        className="scroll-arrow-left"
                        style={{
                          position: "absolute",
                          left: "-16px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                          backgroundColor: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: "50%",
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          opacity: (scrollPositions[key] || 0) <= 0 ? 0.5 : 1,
                        }}
                        onClick={() => {
                          const container = document.getElementById(`scroll-container-${key}`);
                          if (container) container.scrollBy({ left: -300, behavior: 'smooth' });
                        }}
                        disabled={(scrollPositions[key] || 0) <= 0}
                      >
                        ‹
                      </button>
                      <button
                        className="scroll-arrow-right"
                        style={{
                          position: "absolute",
                          right: "-16px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 10,
                          backgroundColor: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: "50%",
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                        onClick={() => {
                          const container = document.getElementById(`scroll-container-${key}`);
                          if (container) container.scrollBy({ left: 300, behavior: 'smooth' });
                        }}
                      >
                        ›
                      </button>
                    </>
                  )}

                  {/* Material Cards Container */}
                  <div
                    id={`scroll-container-${key}`}
                    style={{
                      display: "flex",
                      overflowX: "auto",
                      gap: "16px",
                      paddingBottom: "8px",
                      scrollbarWidth: "thin",
                    }}
                    onScroll={(e) => {
                      const scrollLeft = (e.target as HTMLElement).scrollLeft;
                      setScrollPositions(prev => ({ ...prev, [key]: scrollLeft }));
                    }}
                    ref={(el) => {
                      if (el) setTimeout(() => checkOverflow(key), 100);
                    }}
                  >
                    {materials.map((material: any) => {
                      const variants = material.variants || [];
                      const selectedVariantId = selectedVariants[material.id] || 
                        variants.find((v: any) => v.isSelected)?.id || 
                        variants[0]?.id || 
                        null;
                      
                      let displayImage: string | null = null;
                      if (variants.length > 0) {
                        const currentVariant = variants.find((v: any) => v.id === selectedVariantId) || variants[0];
                        displayImage = currentVariant?.images?.[0]?.url || null;
                      } else if (material.images?.length > 0) {
                        displayImage = material.images[0]?.url || null;
                      }

                      return (
                        <div
                          key={material.id}
                          style={{
                            flexShrink: 0,
                            width: "280px",
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: material.isSelected ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                            boxShadow: material.isSelected ? "0 4px 6px rgba(59, 130, 246, 0.2)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => openMaterialPopup(material)}
                          onMouseEnter={(e) => {
                            if (!material.isSelected) {
                              e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.15)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!material.isSelected) {
                              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                            }
                          }}
                        >
                          {/* Image Section */}
                          <div style={{ position: "relative", width: "100%", height: "160px", backgroundColor: "#f3f4f6" }}>
                            {displayImage ? (
                              <img
                                src={displayImage}
                                alt={material.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
                                No image
                              </div>
                            )}
                            {/* Price Tag */}
                            <div style={{ position: "absolute", top: "8px", right: "8px", backgroundColor: "rgba(255, 255, 255, 0.95)", borderRadius: "6px", padding: "4px 8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                              <div style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937" }}>
                                ${formatCurrency(material.price)}
                              </div>
                              <div style={{ fontSize: "10px", color: "#6b7280" }}>total</div>
                            </div>
                          </div>

                          {/* Content */}
                          <div style={{ padding: "12px" }}>
                            <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", margin: "0 0 4px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {material.name}
                            </h4>
                            <p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 8px 0" }}>
                              {material.brand} • ${formatCurrency(getMaterialUnitPrice(material) || material.price)}/unit
                            </p>

                            {/* Variants */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <div style={{ display: "flex", gap: "4px", overflowX: "auto", maxWidth: "150px" }}>
                                {variants.length > 0 ? (
                                  variants.slice(0, 5).map((variant: any) => (
                                    <button
                                      key={variant.id}
                                      style={{
                                        flexShrink: 0,
                                        width: "28px",
                                        height: "28px",
                                        borderRadius: "4px",
                                        border: selectedVariantId === variant.id ? "2px solid #3b82f6" : "1px solid #d1d5db",
                                        overflow: "hidden",
                                        padding: 0,
                                        cursor: "pointer",
                                        backgroundColor: "#f9fafb",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedVariants(prev => ({ ...prev, [material.id]: variant.id }));
                                      }}
                                      title={variant.color}
                                    >
                                      {variant.images?.[0]?.url ? (
                                        <img
                                          src={variant.images[0].url}
                                          alt={variant.color}
                                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />
                                      ) : (
                                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8px", color: "#6b7280" }}>
                                          {variant.color?.substring(0, 2)}
                                        </div>
                                      )}
                                    </button>
                                  ))
                                ) : (
                                  <span style={{ fontSize: "10px", color: "#9ca3af" }}>No options</span>
                                )}
                              </div>

                              <button
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  border: "none",
                                  cursor: "pointer",
                                  backgroundColor: material.isSelected ? "#3b82f6" : "#e5e7eb",
                                  color: material.isSelected ? "#fff" : "#1f2937",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  chooseMaterial(material.id, selectedVariantId);
                                }}
                                onMouseEnter={(e) => {
                                  if (!material.isSelected) {
                                    e.currentTarget.style.backgroundColor = "#d1d5db";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!material.isSelected) {
                                    e.currentTarget.style.backgroundColor = "#e5e7eb";
                                  }
                                }}
                              >
                                {material.isSelected ? "Selected" : "Select"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <Spacer size={3} direction="col" />

        {/* Material Detail Popup Modal */}
        {isPopupOpen && selectedMaterialForPopup && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
            }}
            onClick={closePopup}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                maxWidth: "1000px",
                width: "100%",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  backgroundColor: "#fff",
                  borderBottom: "1px solid #e5e7eb",
                  padding: window.innerWidth < 768 ? "16px" : "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={{ fontSize: window.innerWidth < 768 ? "18px" : "24px", fontWeight: "700", color: "#1f2937", margin: 0, wordBreak: "break-word" }}>
                    {selectedMaterialForPopup.name}
                  </h2>
                  <p style={{ fontSize: window.innerWidth < 768 ? "12px" : "14px", color: "#6b7280", margin: "4px 0 0 0" }}>
                    {selectedMaterialForPopup.brand} • {getQualityLabel(selectedMaterialForPopup.quality as Quality)} Quality
                  </p>
                </div>
                <button
                  onClick={closePopup}
                  style={{
                    padding: "8px",
                    borderRadius: "50%",
                    border: "none",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span style={{ fontSize: "24px", color: "#6b7280" }}>×</span>
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div style={{ padding: window.innerWidth < 768 ? "16px" : "24px", overflowY: "auto", flex: 1 }}>
                <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: window.innerWidth < 768 ? "20px" : "32px" }}>
                  {/* Left Side - Images and Variants */}
                  <div>
                    {/* Main Image */}
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "320px",
                        backgroundColor: "#f3f4f6",
                        borderRadius: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      {(() => {
                        const variants = selectedMaterialForPopup.variants || [];
                        const selectedVariantId =
                          selectedVariants[selectedMaterialForPopup.id] ||
                          variants.find((v: any) => v.isSelected)?.id ||
                          variants[0]?.id ||
                          null;

                        let displayImage: string | null = null;

                        if (variants.length > 0) {
                          const currentVariant =
                            variants.find((v: any) => v.id === selectedVariantId) || variants[0];
                          displayImage = currentVariant?.images?.[0]?.url || null;
                        } else if (selectedMaterialForPopup.images?.length > 0) {
                          displayImage = selectedMaterialForPopup.images[0]?.url || null;
                        }

                        return displayImage ? (
                          <img
                            src={displayImage}
                            alt={selectedMaterialForPopup.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "12px",
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#9ca3af",
                            }}
                          >
                            <span style={{ fontSize: "48px" }}>📷</span>
                            <p style={{ marginTop: "8px" }}>No image available</p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Selected Variant Label */}
                    {(() => {
                      const variants = selectedMaterialForPopup.variants || [];
                      const selectedVariantId =
                        selectedVariants[selectedMaterialForPopup.id] ||
                        variants.find((v: any) => v.isSelected)?.id ||
                        variants[0]?.id ||
                        null;
                      let selectedVariantLabel: string | null = null;
                      if (variants.length > 0) {
                        const currentVariant =
                          variants.find((v: any) => v.id === selectedVariantId) || variants[0];
                        selectedVariantLabel = currentVariant?.color || null;
                      }
                      return variants.length > 0 && selectedVariantLabel ? (
                        <div style={{ textAlign: "center", marginBottom: "16px" }}>
                          <span style={{ fontSize: "16px", fontWeight: "600", color: "#1f2937" }}>
                            Selected variant:{" "}
                            <span style={{ color: "#3b82f6" }}>{selectedVariantLabel}</span>
                          </span>
                        </div>
                      ) : null;
                    })()}

                    {/* Variants Grid */}
                    {selectedMaterialForPopup.variants && selectedMaterialForPopup.variants.length > 0 && (
                      <div>
                        <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", marginBottom: "12px" }}>
                          Available Options
                        </h3>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
                          {selectedMaterialForPopup.variants.map((variant: any) => (
                            <button
                              key={variant.id}
                              style={{
                                width: "100%",
                                aspectRatio: "1",
                                borderRadius: "8px",
                                overflow: "hidden",
                                position: "relative",
                                border:
                                  (selectedVariants[selectedMaterialForPopup.id] ||
                                    selectedMaterialForPopup.variants.find((v: any) => v.isSelected)?.id ||
                                    selectedMaterialForPopup.variants[0]?.id) === variant.id
                                    ? "2px solid #3b82f6"
                                    : "1px solid #d1d5db",
                                cursor: "pointer",
                                padding: 0,
                                backgroundColor: "#f9fafb",
                                transition: "all 0.2s ease",
                              }}
                              onClick={() => {
                                setSelectedVariants((prev) => ({
                                  ...prev,
                                  [selectedMaterialForPopup.id]: variant.id,
                                }));
                              }}
                              title={variant.color}
                              onMouseEnter={(e) => {
                                if (
                                  (selectedVariants[selectedMaterialForPopup.id] ||
                                    selectedMaterialForPopup.variants.find((v: any) => v.isSelected)?.id ||
                                    selectedMaterialForPopup.variants[0]?.id) !== variant.id
                                ) {
                                  e.currentTarget.style.borderColor = "#9ca3af";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (
                                  (selectedVariants[selectedMaterialForPopup.id] ||
                                    selectedMaterialForPopup.variants.find((v: any) => v.isSelected)?.id ||
                                    selectedMaterialForPopup.variants[0]?.id) !== variant.id
                                ) {
                                  e.currentTarget.style.borderColor = "#d1d5db";
                                }
                              }}
                            >
                              {variant.images?.[0]?.url ? (
                                <img
                                  src={variant.images[0].url}
                                  alt={variant.color}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    textAlign: "center",
                                    padding: "4px",
                                  }}
                                >
                                  {variant.color}
                                </div>
                              )}
                              {(selectedVariants[selectedMaterialForPopup.id] ||
                                selectedMaterialForPopup.variants.find((v: any) => v.isSelected)?.id ||
                                selectedMaterialForPopup.variants[0]?.id) === variant.id && (
                                <div
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    backgroundColor: "rgba(59, 130, 246, 0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: "#3b82f6",
                                      borderRadius: "50%",
                                      padding: "4px",
                                    }}
                                  >
                                    <span style={{ color: "#fff", fontSize: "16px" }}>✓</span>
                                  </div>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Side - Details and Actions */}
                  <div>
                    {/* Pricing */}
                    <div style={{ marginBottom: "24px" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "8px" }}>
                        <span style={{ fontSize: "32px", fontWeight: "700", color: "#1f2937" }}>
                          ${formatCurrency(selectedMaterialForPopup.price)}
                        </span>
                        <span style={{ fontSize: "14px", color: "#6b7280" }}>total</span>
                      </div>
                      <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
                        ${formatCurrency(getMaterialUnitPrice(selectedMaterialForPopup) || selectedMaterialForPopup.price)} per unit
                      </p>
                    </div>

                    {/* Description */}
                    {selectedMaterialForPopup.description && (
                      <div style={{ marginBottom: "24px" }}>
                        <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", marginBottom: "8px" }}>
                          Description
                        </h3>
                        <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: "1.6", margin: 0 }}>
                          {selectedMaterialForPopup.description}
                        </p>
                      </div>
                    )}

                    {/* Material Details */}
                    <div style={{ marginBottom: "32px" }}>
                      <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", marginBottom: "12px" }}>
                        Material Details
                      </h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingBottom: "8px",
                            borderBottom: "1px solid #f3f4f6",
                          }}
                        >
                          <span style={{ fontSize: "14px", color: "#6b7280" }}>Manufacturer:</span>
                          <span style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>
                            {selectedMaterialForPopup.brand || "N/A"}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingBottom: "8px",
                            borderBottom: "1px solid #f3f4f6",
                          }}
                        >
                          <span style={{ fontSize: "14px", color: "#6b7280" }}>Quality:</span>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              padding: "4px 12px",
                              borderRadius: "16px",
                              backgroundColor:
                                selectedMaterialForPopup.quality?.toUpperCase() === "HIGH"
                                  ? "#d1fae5"
                                  : selectedMaterialForPopup.quality?.toUpperCase() === "MEDIUM"
                                  ? "#fef3c7"
                                  : "#dbeafe",
                              color:
                                selectedMaterialForPopup.quality?.toUpperCase() === "HIGH"
                                  ? "#065f46"
                                  : selectedMaterialForPopup.quality?.toUpperCase() === "MEDIUM"
                                  ? "#92400e"
                                  : "#1e40af",
                            }}
                          >
                            {getQualityLabel(selectedMaterialForPopup.quality as Quality)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingBottom: "8px",
                            borderBottom: "1px solid #f3f4f6",
                          }}
                        >
                          <span style={{ fontSize: "14px", color: "#6b7280" }}>Unit Price:</span>
                          <span style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>
                            ${formatCurrency(getMaterialUnitPrice(selectedMaterialForPopup) || selectedMaterialForPopup.price)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: "flex", flexDirection: window.innerWidth < 768 ? "column" : "row", gap: "12px" }}>
                      <button
                        style={{
                          flex: 1,
                          padding: window.innerWidth < 768 ? "14px 20px" : "12px 24px",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          border: "none",
                          cursor: "pointer",
                          backgroundColor: selectedMaterialForPopup.isSelected ? "#3b82f6" : "#3b82f6",
                          color: "#fff",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => {
                          const selectedVariantId =
                            selectedVariants[selectedMaterialForPopup.id] ||
                            selectedMaterialForPopup.variants?.find((v: any) => v.isSelected)?.id ||
                            selectedMaterialForPopup.variants?.[0]?.id ||
                            null;
                          chooseMaterial(selectedMaterialForPopup.id, selectedVariantId);
                          closePopup();
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#2563eb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#3b82f6";
                        }}
                      >
                        {selectedMaterialForPopup.isSelected ? "Selected" : "Select This Material"}
                      </button>
                      <button
                        style={{
                          padding: "12px 24px",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          border: "1px solid #d1d5db",
                          cursor: "pointer",
                          backgroundColor: "#fff",
                          color: "#6b7280",
                          transition: "all 0.2s ease",
                        }}
                        onClick={closePopup}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff";
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  // Main material shop view
  return (
    <div
      ref={topRef}
      style={{
        padding: window.innerWidth < 768 ? "16px" : "24px",
        backgroundColor: "#f8fafc",
        minHeight: "100%",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Back Button and Breadcrumb */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            borderRadius: "6px",
            backgroundColor: "#3b82f6",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <ArrowBack style={{ width: "20px", height: "20px" }} />
          Back
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "14px", color: "#1f2937" }}>Material selection /</span>
          <span style={{ fontSize: "14px", color: "#6b7280" }}>
            {selectedProduct.name?.charAt(0).toUpperCase() + selectedProduct.name?.slice(1).toLowerCase()}
          </span>
        </div>
      </div>

      {/* Quality Level Selection */}
      <div style={{ marginBottom: "32px", width: "100%" }}>
        <h2 style={{ fontSize: "16px", fontWeight: "500", color: "#1f2937", marginBottom: "8px" }}>
          Quality level
        </h2>
        <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>
          This default helps us auto select items for you that don't require color or design input, but just quality level.
        </p>
        <div
          style={{
            display: "flex",
            backgroundColor: "#f3f3f3",
            height: "50px",
            borderRadius: "8px",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "0 8px",
          }}
        >
          {qualityLevels.map((level, index) => (
            <div
              key={level}
              onClick={() => handleQualityChange(level)}
              style={{
                flex: 1,
                height: "60%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "6px",
                cursor: "pointer",
                backgroundColor: quality === level ? "#3b82f6" : "#f3f3f3",
                color: quality === level ? "#fff" : "#3b82f6",
                border: quality === level ? "none" : "1px solid #3b82f6",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.2s ease",
              }}
            >
              {getQualityLabel(level)}
            </div>
          ))}
        </div>
      </div>

      {/* Selection Required Section */}
      {requiredSelectionMeasurements.length > 0 && (
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "500", color: "#1f2937", marginBottom: "16px" }}>
            Selection Required
          </h3>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {requiredSelectionMeasurements.map((measure) => renderMeasurementCard(measure, true))}
          </div>
        </div>
      )}

      {/* Selection Optional Section */}
      {(optionalSelectionMeasurements.length > 0 || showAllAsOptional) && (
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "500", color: "#1f2937", marginBottom: "8px" }}>
            {showAllAsOptional ? "Material Measurements" : "Selection Optional"}
          </h3>
          <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "16px", width: "100%" }}>
            {showAllAsOptional 
              ? "Select materials for the following measurements in your project."
              : "Selection not required. These items are standard and are selected for you by default based on your project type."
            }
          </p>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {(showAllAsOptional ? allMeasurements : optionalSelectionMeasurements).map((measure) => 
              renderMeasurementCard(measure, false)
            )}
          </div>
        </div>
      )}

      {/* No Measurements Message */}
      {!showAllAsOptional && requiredSelectionMeasurements.length === 0 && optionalSelectionMeasurements.length === 0 && (
        <div style={{ marginBottom: "32px" }}>
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "2px dashed #d1d5db",
              borderRadius: "12px",
              padding: "48px 24px",
              textAlign: "center",
              color: "#6b7280",
              maxWidth: "600px",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: "500",
                color: "#374151",
              }}
            >
              No Measurements Available
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              No measurements have been configured for this product yet.
              Please contact your project manager for more information.
            </p>
          </div>
        </div>
      )}

      {/* Support Section */}
      <div style={{ marginTop: "40px", textAlign: "center" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 16px",
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#6b7280",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
            e.currentTarget.style.borderColor = "#d1d5db";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          <img
            src="https://imagedelivery.net/ljlAKBP_91m1yO7rrhjKgQ/3460b5f5-0adf-4ecd-b334-288411dfe400/chatImage"
            alt="Support"
            style={{ width: 12, height: 12 }}
          />
          <span>Missing something? Contact your support specialist.</span>
        </div>
      </div>

      <Spacer size={3} direction="col" />
    </div>
  );
};
